import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, Flag, Briefcase } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export interface Candidate {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  assessmentTitle: string;
  status: 'invited' | 'in_progress' | 'completed';
  completedDate?: string | null;
  displayDate?: string;
  created_at?: string;
  score?: number | null;
  aiRating?: number | null;
  potentialPlagiarism?: boolean;
  skill_ratings?: Array<{ skill: string; rating: number }> | null;
  ai_summary?: string | null;
  source?: string; // 'RoundOne' or 'ATS'
  fit_status?: 'good_fit' | 'maybe' | 'not_fit' | null;
}

interface CandidateCardProps {
  candidate: Candidate;
  className?: string;
  isInAssessmentView?: boolean;
  onClick?: (id: string) => void;
  isSelected?: boolean;
}

const CandidateCard: React.FC<CandidateCardProps> = ({ 
  candidate, 
  className = '',
  isInAssessmentView = false,
  onClick,
  isSelected = false
}) => {
  const navigate = useNavigate();

  const getStatusLabel = (status: Candidate['status']) => {
    switch (status) {
      case 'invited': return 'Invited';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
    }
  };
  
  const getStatusColor = (status: Candidate['status']) => {
    switch (status) {
      case 'invited': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getFitStatusColor = (status?: string | null) => {
    switch (status) {
      case 'good_fit': return 'bg-green-100 text-green-800 border-green-200';
      case 'maybe': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_fit': return 'bg-red-100 text-red-800 border-red-200';
      default: return '';
    }
  };
  
  const getFitStatusLabel = (status?: string | null) => {
    switch (status) {
      case 'good_fit': return 'Good Fit';
      case 'maybe': return 'Maybe';
      case 'not_fit': return 'Not Fit';
      default: return '';
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const renderStars = (rating?: number | null) => {
    if (rating === undefined || rating === null) {
      // Show empty stars if no rating
      return (
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              size={14}
              className="text-gray-300"
            />
          ))}
          <span className="ml-1 text-xs font-medium text-muted-foreground">Not rated</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => {
          // Calculate if this star should be full, half, or empty
          const isFullStar = star <= Math.floor(rating);
          const isHalfStar = !isFullStar && star <= Math.ceil(rating) && (star - 0.5) <= rating;
          
          return (
            <Star
              key={star}
              size={14}
              className={`${
                isFullStar
                  ? 'text-yellow-400 fill-yellow-400'
                  : isHalfStar
                  ? 'text-yellow-400 fill-yellow-400 opacity-50'
                  : 'text-gray-300'
              } transition-colors`}
            />
          );
        })}
        <span className="ml-1 text-xs font-medium text-muted-foreground">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(candidate.id);
    } else {
      navigate(`/candidates/${candidate.id}`);
    }
  };

  // Get source badge similar to how it's shown in AssessmentCard
  const renderSourceBadge = () => {
    if (!candidate.source) return null;
    
    return (
      <Badge 
        variant="outline" 
        className={candidate.source === 'ATS' ? 'bg-blue-50 text-blue-600 text-xs' : 'bg-card text-xs'}
      >
        <Briefcase className="w-3 h-3 mr-1" />
        {candidate.source}
      </Badge>
    );
  };

  return (
    <Card 
      className={`${className} ${isSelected ? 'border-primary' : ''} hover:border-primary/50 transition-colors cursor-pointer relative overflow-hidden`}
      onClick={handleCardClick}
    >
      {candidate.potentialPlagiarism && (
        <div className="absolute bottom-1 right-1 z-10">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-red-500 text-white p-1 rounded-full shadow-lg">
                  <Flag size={16} fill="white" className="text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-red-500 text-white border-red-600">
                <p className="text-xs font-bold">⚠️ Potential AI usage detected!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      
      <CardHeader className="pb-2 pt-3 px-4 flex flex-col space-y-2">
        <div className="flex justify-between items-start w-full">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <Avatar className="h-8 w-8 flex-shrink-0 border border-border">
              <AvatarImage src={candidate.avatarUrl} alt={candidate.name} />
              <AvatarFallback className="bg-roundone-purple/10 text-roundone-purple text-xs">
                {getInitials(candidate.name)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="font-medium text-sm hover:text-roundone-purple text-wrap pr-2">
                {candidate.name}
              </div>
              <div className="text-xs text-muted-foreground truncate">{candidate.email}</div>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className={`${getStatusColor(candidate.status)} capitalize text-xs font-normal py-0.5 whitespace-nowrap flex-shrink-0`}>
              {getStatusLabel(candidate.status)}
            </Badge>
            
            {candidate.fit_status && candidate.status === 'completed' && (
              <Badge 
                variant="outline" 
                className={`${getFitStatusColor(candidate.fit_status)} capitalize text-xs font-normal py-0.5 whitespace-nowrap flex-shrink-0`}
              >
                {getFitStatusLabel(candidate.fit_status)}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="w-full space-y-2">
          {renderStars(candidate.aiRating)}
          <div className="pt-1 flex space-x-2 items-center">
            {renderSourceBadge()}
          </div>
        </div>
      </CardHeader>

      {!isInAssessmentView && candidate.assessmentTitle && (
        <CardContent className="pt-0 pb-2 px-6">
          <div className="text-xs text-muted-foreground border-t pt-2 mt-1 truncate">
            <span className="font-medium text-muted-foreground/80">Assessment:</span> {candidate.assessmentTitle}
          </div>
        </CardContent>
      )}

      {candidate.completedDate && (
        <p className="text-xs text-muted-foreground">
          Completed {candidate.displayDate ? candidate.displayDate : formatDistanceToNow(new Date(candidate.completedDate))} ago
        </p>
      )}
    </Card>
  );
};

export default CandidateCard;
